import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {getPricingContent} from '@/content/pricing'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {isPartiallySupportedLocale} from '@/utils/constants'
import {useLanguage} from '@/utils/i18n/language'
import {analytics} from '@/utils/tracking/analytics'
import {AnalyticsTrackedEvent} from '@/utils/tracking/types'

import {PricingCounterField} from './PricingCounterField'

type PricingClassSelectProps = {
  selectedClasses: number
  setSelectedClasses: (selectedClasses: number) => void
  minSelectedClasses: number
  trackedEvent: AnalyticsTrackedEvent
  title?: {textVariant?: TextVariant; color?: string; fontWeight?: TextFontWeight}
  hasLearnMore?: boolean
}

export const PricingClassSelect: FC<PricingClassSelectProps> = ({
  selectedClasses,
  setSelectedClasses,
  minSelectedClasses,
  trackedEvent,
  title,
  hasLearnMore = true,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {selectedClassesTitle, learnMoreAboutClassesLinkText, learnMoreAboutClassesLinkHref} =
    getPricingContent().calculator

  const linkHrefByLocale =
    !hasLearnMore || isPartiallySupportedLocale(language)
      ? undefined
      : learnMoreAboutClassesLinkHref[language]

  return (
    <>
      <PricingCounterField
        title={{text: t(selectedClassesTitle), ...title}}
        value={selectedClasses}
        setValue={setSelectedClasses}
        minValue={minSelectedClasses}
        trackedEvent={trackedEvent}
        description={
          linkHrefByLocale && (
            <Text variant={TextVariant.text14}>
              <Link href={linkHrefByLocale} legacyBehavior>
                <a
                  className="link"
                  target="_blank"
                  role="link"
                  tabIndex={0}
                  onClick={() => analytics.track('Learn more about trademark classes is clicked')}
                >
                  {t(learnMoreAboutClassesLinkText)}
                </a>
              </Link>
            </Text>
          )
        }
      />

      <style jsx>{`
        .link {
          color: ${theme.palette.info.main};
          text-decoration: underline;
        }
        .link:hover {
          text-decoration: none;
        }
      `}</style>
    </>
  )
}
